import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandNav from "../components/BrandNav";
import ChapterCard from "../components/ChapterCard";
import MySubmissions from "../components/MySubmissions";
import MyCertificates from "../components/MyCertificates";
import MyPayments from "../components/MyPayments";
import { api } from "../lib/api";
import { findBand } from "../lib/palette";

export default function Dashboard() {
  const nav = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [current, setCurrent] = useState(null); // enrollment obj
  const [term, setTerm] = useState(1);
  const [lessons, setLessons] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("lessons"); // lessons | submissions | certificates | payments

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/enrollments/me");
        if (!alive) return;
        setEnrollments(data.enrollments || []);
        if (!data.enrollments?.length) {
          nav("/pick-band", { replace: true });
          return;
        }
        setCurrent(data.enrollments[0]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [nav]);

  // Fetch lessons + chapters in parallel whenever band or term changes
  useEffect(() => {
    if (!current) return;
    let alive = true;
    Promise.all([
      api.get(`/catalog/${current.age_band}/${term}/lessons`),
      api.get(`/readers/${current.age_band}/term_${term}`),
    ])
      .then(([lessonsResp, chaptersResp]) => {
        if (!alive) return;
        setLessons(lessonsResp.data.lessons || []);
        setChapters(chaptersResp.data.chapters || []);
      })
      .catch(() => {
        if (!alive) return;
        setLessons([]);
        setChapters([]);
      });
    return () => { alive = false; };
  }, [current, term]);

  const band = current ? findBand(current.age_band) : null;
  const trialCap = current?.tier === "trial";

  const lessonById = useMemo(
    () => Object.fromEntries((lessons || []).map((l) => [l.id, l])),
    [lessons]
  );

  // (lessonId) → { done, unlocked } — same rules as before, per lesson.
  const lessonState = useMemo(() => {
    const progress = current?.lesson_progress || {};
    const termsUnlocked = current?.terms_unlocked || [];
    return (lessonId) => {
      const l = lessonById[lessonId];
      if (!l) return { done: false, unlocked: false };
      const done = !!progress[lessonId]?.completed_at;
      const termUnlocked = termsUnlocked.includes(l.term);
      const trialAllowed = !trialCap || (l.term === 1 && l.week === 1);
      return { done, unlocked: !!termUnlocked && trialAllowed };
    };
  }, [current, lessonById, trialCap]);

  const scrollToUpgrade = () => {
    // Navigate home + hash — Landing page has the pricing anchor.
    nav("/#pricing");
  };

  if (loading || !current || !band) {
    return (
      <div>
        <BrandNav />
        <div className="ftg-loader" data-testid="dashboard-loader">
          <div className="ftg-loader__dot" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <div className="ftg-dash__head">
          <div>
            <h1 className="ftg-dash__title" data-testid="dashboard-title">
              {band.label}
            </h1>
            <p className="ftg-dash__meta" data-testid="dashboard-meta">
              {current.tier === "trial" ? "Free preview" : `Tier: ${current.tier}`}
              {" · "}
              Terms unlocked: {current.terms_unlocked.join(", ")}
            </p>
          </div>
          <div style={{ color: band.accent, fontWeight: 700 }} data-testid="dashboard-band-tag">
            Learning band {band.label}
          </div>
        </div>

        <div className="ftg-dash__view-tabs" data-testid="dash-view-tabs">
          <button type="button" onClick={() => setView("lessons")}
            className={`ftg-dash__view-tab${view === "lessons" ? " is-active" : ""}`}
            data-testid="view-tab-lessons">Lessons</button>
          <button type="button" onClick={() => setView("submissions")}
            className={`ftg-dash__view-tab${view === "submissions" ? " is-active" : ""}`}
            data-testid="view-tab-submissions">My submissions</button>
          <button type="button" onClick={() => setView("certificates")}
            className={`ftg-dash__view-tab${view === "certificates" ? " is-active" : ""}`}
            data-testid="view-tab-certificates">My certificates</button>
          <button type="button" onClick={() => setView("payments")}
            className={`ftg-dash__view-tab${view === "payments" ? " is-active" : ""}`}
            data-testid="view-tab-payments">Payments</button>
        </div>

        {view === "submissions" ? (
          <MySubmissions />
        ) : view === "certificates" ? (
          <MyCertificates />
        ) : view === "payments" ? (
          <MyPayments />
        ) : (
          <>
            <div className="ftg-dash__term-tabs" data-testid="term-tabs">
              {[1, 2, 3].map((t) => {
                const isActive = t === term;
                const locked = !current.terms_unlocked.includes(t);
                return (
                  <button key={t} onClick={() => !locked && setTerm(t)}
                    className={`ftg-dash__term-tab${isActive ? " is-active" : ""}${locked ? " is-locked" : ""}`}
                    disabled={locked} data-testid={`term-tab-${t}`}>
                    Term {t} {locked ? "· locked" : ""}
                  </button>
                );
              })}
            </div>

            <div className="ftg-chapter-list" data-testid="chapter-list">
              {chapters.length === 0 ? (
                <div className="ftg-chapter-list__loading">Loading chapters…</div>
              ) : (
                chapters.map((ch) => {
                  const lessonsForChapter = (ch.lesson_ids || [])
                    .map((id) => lessonById[id])
                    .filter(Boolean);
                  return (
                    <ChapterCard
                      key={ch.chapter_num}
                      chapter={ch}
                      ageBand={current.age_band}
                      termInt={term}
                      lessons={lessonsForChapter}
                      lessonState={lessonState}
                      accent={band.accent}
                      onUpgradeClick={scrollToUpgrade}
                    />
                  );
                })
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
